import {
  Box,
  Link,
  ChevronLeftIcon,
  Heading,
  Text,
  Button,
  Alert,
  Table,
  TableRow,
  TableCell,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableToolbar,
  TableToolbarActions,
  Tooltip,
  TooltipInteractiveWrapper,
  InfoIcon,
  TableBody,
  Badge,
  IconButton,
  EditIcon,
  RefreshIcon,
  Spinner,
  useToast,
} from '@razorpay/blade/components';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import {
  GET_LOAN_SETTINGS,
  LIST_LOAN_CATEGORIES,
  TOGGLE_LOAN_CATEGORY,
  UPDATE_LOAN_SETTINGS,
} from './constants';
import { CategoryData, LoanCategory } from './types';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { CreateEmployeeLoansCategory } from './CreateEmployeeLoansCategory';
import { ToggleLoanCategoryStatusConfirmationModal } from './CreateEmployeeLoansCategory/ToggleLoanCategoryStatusConfirmationModal';
import { EnableRequestsSuccessModal } from './EnableRequestsSuccessModal';

export const EmployeeLoansSettings = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<LoanCategory | undefined>(undefined);
  const [isToggleCategoryModalOpen, setIsToggleCategoryModalOpen] = useState(false);
  const [selectedCategoryData, setSelectedCategoryData] = useState<CategoryData>();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isLoanRequestsEnabled, setIsLoanRequestsEnabled] = useState(false);

  const { isLoading: isLoanSettingsLoading, isError: loanSettingError } = useQuery({
    queryKey: GET_LOAN_SETTINGS,
    queryFn: api.loans.getLoanSettings,
    onSuccess: (response) => {
      setIsLoanRequestsEnabled(response.is_loan_requests_enabled);
    },
  });

  const {
    data: categoriesData,
    refetch: refetchCategories,
    isLoading: isCategoriesDataLoading,
    isFetching: isCategoriesDataFetching,
  } = useQuery({
    queryKey: LIST_LOAN_CATEGORIES,
    queryFn: () => api.loans.listLoanCategories(),
  });

  const updateLoanSettings = useMutation({
    mutationKey: UPDATE_LOAN_SETTINGS,
    mutationFn: api.loans.updateLoanSettings,
    onSuccess: (_, variables) => {
      setIsLoanRequestsEnabled(variables.is_loan_requests_enabled);
      if (variables.is_loan_requests_enabled) {
        setIsSuccessModalOpen(true);
      } else {
        showToast({
          content: 'Employee loan requests disabled.',
          color: 'positive',
          duration: 4000,
        });
      }
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to update loan request settings. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleToggleLoanRequests = () => {
    updateLoanSettings.mutate({ is_loan_requests_enabled: !isLoanRequestsEnabled });
  };

  const toggleCategoryMutation = useMutation<
    { success: boolean },
    typeof AppError,
    { categoryId: number; isActive: boolean }
  >({
    mutationKey: TOGGLE_LOAN_CATEGORY,
    mutationFn: (variables) => api.loans.toggleLoanCategory(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(LIST_LOAN_CATEGORIES);
      setIsToggleCategoryModalOpen(false);
      showToast({
        content: `Category ${variables.isActive ? 'enabled' : 'disabled'} successfully.`,
        color: 'positive',
        duration: 4000,
      });
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to update category status. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const downloadDocumentMutation = useMutation({
    mutationFn: (fileId: string) => api.loans.downloadLoanDocument(fileId),
    onSuccess: (res) => {
      const a = document.createElement('a');
      a.href = res.signed_url;
      a.target = '_blank';
      a.download = '';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloadingFileId(null);
    },
    onError: (error: typeof AppError) => {
      setDownloadingFileId(null);
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to download document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleDownloadDocument = (fileId: string) => {
    setDownloadingFileId(fileId);
    downloadDocumentMutation.mutate(fileId);
  };

  const handleCreateCategoryClick = () => {
    setEditCategory(undefined);
    setIsCreateCategoryOpen(true);
  };

  const handleEditCategoryClick = (category: LoanCategory) => {
    setEditCategory(category);
    setIsCreateCategoryOpen(true);
  };

  const handleCreateCategoryClose = () => {
    setIsCreateCategoryOpen(false);
    setEditCategory(undefined);
  };

  const handleCreateCategorySuccess = () => {
    setIsCreateCategoryOpen(false);
    setEditCategory(undefined);
  };

  const handleToggleStatus = ({
    categoryId,
    categoryName,
    categoryStatus,
  }: {
    categoryId: string | number;
    categoryName: string;
    categoryStatus: string;
  }) => {
    setSelectedCategoryData({
      categoryId,
      categoryName,
      categoryStatus,
    });
    setIsToggleCategoryModalOpen(true);
  };

  if (isLoanSettingsLoading && isCategoriesDataLoading) {
    return <Spinner accessibilityLabel="" />;
  }

  return (
    <Box paddingY={'spacing.5'} paddingX={'spacing.10'}>
      <Link onClick={() => history.goBack()} icon={ChevronLeftIcon}>
        Back{' '}
      </Link>
      <Heading size="large" weight="semibold" color="surface.text.gray.normal">
        Loans & Advances
      </Heading>
      {!loanSettingError ? (
        <Box
          display="flex"
          flexDirection="column"
          borderWidth="thin"
          borderRadius="large"
          backgroundColor="surface.background.gray.intense"
          marginTop="spacing.8">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="spacing.7">
            <Box display="flex" flexDirection="column" gap="spacing.3">
              <Text size="medium" variant="body" weight="semibold" color="surface.text.gray.subtle">
                Allow Employee Requests{' '}
                {isLoanRequestsEnabled ? (
                  <Badge marginLeft="spacing.2" size="medium" color="positive" emphasis="subtle">
                    Enabled
                  </Badge>
                ) : null}
              </Text>
              <Text
                size="small"
                variant="body"
                weight="medium"
                color="surface.text.gray.muted"
                as="div">
                Employees can submit loan requests through their employee portal. All requests
                require your approval before processing.
              </Text>
            </Box>
            <Box display="flex" alignItems="center" gap="spacing.5">
              <Button
                size="small"
                variant={isLoanRequestsEnabled ? 'tertiary' : 'primary'}
                isLoading={updateLoanSettings.isLoading || isLoanSettingsLoading}
                onClick={handleToggleLoanRequests}>
                {isLoanRequestsEnabled ? 'Disable Requests' : 'Enable Requests'}
              </Button>
            </Box>
          </Box>
          {!isLoanRequestsEnabled ? (
            <Alert
              color="information"
              description="Turn 3-day processing into 30-second approvals. Employees self-serve, you approve with one click."
              isDismissible={false}
              isFullWidth
            />
          ) : null}
        </Box>
      ) : null}

      <Box marginTop={'64px'} width={'100%'}>
        <Box
          display="flex"
          justifyContent={'space-between'}
          alignItems={'center'}
          marginBottom="spacing.5">
          <Box display="flex" flexDirection={'column'}>
            <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
              Categories
            </Heading>
            <Text size="medium" variant="body" weight="regular" color="surface.text.gray.muted">
              Define different loan types with terms, limits, and eligibility criteria. Each
              category can have its own policy document and rules.
            </Text>
          </Box>
          <Button variant="tertiary" size="small" onClick={handleCreateCategoryClick}>
            Create Category
          </Button>
        </Box>

        <CreateEmployeeLoansCategory
          isOpen={isCreateCategoryOpen}
          onClose={handleCreateCategoryClose}
          onSuccess={handleCreateCategorySuccess}
          editCategory={editCategory}
        />

        <Table<LoanCategory>
          data={{ nodes: categoriesData?.data ?? [] }}
          isLoading={isCategoriesDataFetching}
          toolbar={
            <TableToolbar
              title={
                categoriesData?.data?.length
                  ? `${categoriesData.data.length} Loan Categories`
                  : 'No items to display'
              }>
              <TableToolbarActions>
                <Button icon={RefreshIcon} variant="tertiary" onClick={() => refetchCategories()} />
              </TableToolbarActions>
            </TableToolbar>
          }
          showBorderedCells={false}>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>
                    <Text weight="semibold">Name</Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" flexDirection="row" gap="spacing.3">
                      <Text weight="semibold">Policy Document</Text>
                      <Tooltip
                        placement="top"
                        content="Policy documents define loan terms, eligibility, and requirements. Uploading them minimizes employee queries and accelerates approvals.">
                        <TooltipInteractiveWrapper>
                          <InfoIcon marginTop="spacing.1" />
                        </TooltipInteractiveWrapper>
                      </Tooltip>
                    </Box>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" flexDirection="row" gap="spacing.3">
                      <Text weight="semibold">Status</Text>
                      <Tooltip
                        placement="top"
                        content="Disabled categories are hidden from employees. New requests cannot be submitted, but active loans will continue repayment as usual.">
                        <TooltipInteractiveWrapper>
                          <InfoIcon marginTop="spacing.1" />
                        </TooltipInteractiveWrapper>
                      </Tooltip>
                    </Box>
                  </TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((category) => (
                  <TableRow
                    item={category}
                    key={category.id}
                    hoverActions={
                      <Box display="flex" gap="spacing.4" alignItems="center">
                        <Button
                          variant="tertiary"
                          color="primary"
                          size="small"
                          onClick={() => {
                            handleToggleStatus({
                              categoryId: category.id,
                              categoryName: category.name,
                              categoryStatus: category.is_active ? 'Enabled' : 'Disabled',
                            });
                          }}
                          testID={`toggle-status-${category.id}`}>
                          {category.is_active ? 'Disable Category' : 'Enable Category'}
                        </Button>
                        <IconButton
                          icon={EditIcon}
                          accessibilityLabel="Edit category details"
                          onClick={() => handleEditCategoryClick(category)}
                          size="medium"
                        />
                      </Box>
                    }>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>
                      {category.policy_file_id ? (
                        <Link
                          isDisabled={downloadingFileId === category.policy_file_id}
                          onClick={() => handleDownloadDocument(category.policy_file_id!)}>
                          {downloadingFileId === category.policy_file_id ? 'Loading...' : 'View'}
                        </Link>
                      ) : (
                        'NA'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge color={category.is_active ? 'positive' : 'neutral'}>
                        {category.is_active ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
        {!categoriesData?.data?.length && (
          <EmptyListView
            title="No Loan Categories created yet"
            description={
              <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                Click on Create Category button to create your first loan category
              </Text>
            }>
            <Box marginX="auto">
              <Button size="medium" onClick={handleCreateCategoryClick}>
                Create Category
              </Button>
            </Box>
          </EmptyListView>
        )}
        {isToggleCategoryModalOpen && !!selectedCategoryData?.categoryId && (
          <ToggleLoanCategoryStatusConfirmationModal
            isOpen={isToggleCategoryModalOpen}
            categoryName={selectedCategoryData?.categoryName || ''}
            onClose={() => setIsToggleCategoryModalOpen(false)}
            onPrimaryButtonClick={() => {
              if (!selectedCategoryData?.categoryId) return;
              toggleCategoryMutation.mutate({
                categoryId: Number(selectedCategoryData.categoryId),
                isActive: selectedCategoryData.categoryStatus.toLowerCase() === 'disabled',
              });
            }}
            isLoading={toggleCategoryMutation.isLoading}
            isDisabled={toggleCategoryMutation.isLoading}
            modalType={
              selectedCategoryData?.categoryStatus?.toLowerCase() === 'enabled'
                ? 'disable'
                : 'enable'
            }
          />
        )}
      </Box>

      <EnableRequestsSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        onCreateCategory={() => {
          setIsSuccessModalOpen(false);
          setIsCreateCategoryOpen(true);
        }}
      />
    </Box>
  );
};
